import React, { useEffect, useRef, useState, useContext } from 'react';
import styles from './SupportPage.module.css';
import Header from '../../components/Header/Header';
import Footer from '../../components/Footer/Footer';
import { HeaderType } from '../../components/Header/types/Header';
import { FooterType } from '../../components/Footer/types/Footer';
import TabButton from '../../components/UI/TabButton/TabButton';
import Button from '../../components/UI/Button/Button';
import cn from 'classnames';
import { useNavigate } from 'react-router-dom';
import { MORTGAGE_PAGE } from '../../router/paths';
import { Helmet } from 'react-helmet';
import PhoneCallModal from '../../components/PhoneCallModal/PhoneCallModal';
import { api } from '../../utils/api';
import Loader from '../../components/Loader/Loader';
import { LoaderContext } from '../../components/Loader/LoaderContext';

interface QuestionAnswer {
  id: number;
  question: string;
  answer: string;
}

interface TabData {
  id: number;
  tabTitle: string;
  questions: QuestionAnswer[];
}

export default function SupportPage() {
  const [activeTab, setActiveTab] = useState(0);
  const [isModalOpened, setIsModalOpened] = useState(false);
  const questionsRef = useRef<HTMLDivElement>(null);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const [faqData, setFaqData] = useState<TabData[]>([]);
  const [error, setError] = useState<string | null>(null);
  const { isInitialLoad, setIsInitialLoad } = useContext(LoaderContext);

  const updateWindowWidth = () => {
    setWindowWidth(window.innerWidth);
  };

  useEffect(() => {
    window.addEventListener('resize', updateWindowWidth);
    return () => {
      window.removeEventListener('resize', updateWindowWidth);
    };
  }, []);

  useEffect(() => {
    const fetchFaqData = async () => {
      try {
        setError(null);
        const response = await api.support.getAll();
        setFaqData(response);
      } catch (err) {
        console.error('Failed to fetch FAQ data:', err);
        setError('Не удалось загрузить данные. Попробуйте обновить страницу.');
      } finally {
        setIsInitialLoad(false);
      }
    };

    fetchFaqData();
  }, [setIsInitialLoad]);

  const handleTabClick = (index: number) => {
    setActiveTab(index);
  };

  const handleModal = () => {
    setIsModalOpened((prev) => !prev);
  };

  const navigate = useNavigate();

  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (target.classList.contains('phone')) {
        e.preventDefault();
        handleModal();
      }
    };

    const questionsContainer = questionsRef.current;
    if (questionsContainer) {
      questionsContainer.addEventListener('click', handleClick);
    }

    return () => {
      if (questionsContainer) {
        questionsContainer.removeEventListener('click', handleClick);
      }
    };
  }, []);

  const buttonStyle = {
    width: windowWidth <= 781 ? '100%' : 'fit-content',
  };

  const renderQuestion = (question: string) => {
    return <p className='text_subheaders_sh2' dangerouslySetInnerHTML={{ __html: question }} />;
  };

  const renderAnswer = (answer: string) => {
    const parser = new DOMParser();
    const doc = parser.parseFromString(answer, 'text/html');

    const links = doc.querySelectorAll("a.phone");
    links.forEach((link) => {
      if (link instanceof HTMLAnchorElement) {
        link.href = "javascript:void(0)";
      }
    });

    return <span dangerouslySetInnerHTML={{ __html: doc.body.innerHTML }} />;
  };

  return (
    <>
      <Helmet>
        <title>Поддержка | Восток Строй</title>
        <meta name="description" content="Мы начали свой путь в 2014 году и реализовали 9 успешных проектов." />
        <meta name="keywords" content="недвижимость, девелопер, Каспийск, новостройки, квартиры" />
        <meta name="author" content="Восток Строй" />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href="https://vostokstroy.com/support" />

        <meta property="og:title" content="Поддержка | Восток Строй" />
        <meta property="og:description" content="Мы начали свой путь в 2014 году и реализовали 9 успешных проектов." />
        <meta property="og:image" content="https://vostokstroy.com/open-graphs/main-opengraph.jpg" />
        <meta property="og:url" content="https://vostokstroy.com/support" />
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="Восток Строй" />

        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Поддержка | Восток Строй" />
        <meta name="twitter:description" content="Мы начали свой путь в 2014 году и реализовали 9 успешных проектов." />
        <meta name="twitter:image" content="https://vostokstroy.com/open-graphs/main-opengraph.jpg" />

        <link rel="icon" type="image/svg+xml" href="/favicons/main-fav-32x32.svg" />
        <link rel="icon" type="image/png" sizes="16x16" href="/favicons/main-fav-16x16.png" />
        <link rel="icon" type="image/png" sizes="32x32" href="/favicons/main-fav-32x32.png" />
        <link rel="apple-touch-icon" sizes="180x180" href="/favicons/main-apple-touch-icon-180x180.png" />
        <link rel="icon" type="image/png" sizes="180x180" href="/favicons/main-fav-180x180.png" />
      </Helmet>

      {isInitialLoad ? (
        <Loader />
      ) : (
        <>
          <Header theme={HeaderType.Light} />
          <div className={styles.content}>
        <div className={styles.content_head}>
          <div className={styles.page_name}>
            <h1>На всё ответим</h1>
          </div>
          {!error && faqData.length > 0 && (
            <div className={styles.tab_group}>
              {faqData.map((tab: TabData, index: number) => (
                <TabButton
                  key={tab.id}
                  isActive={activeTab === index}
                  onClick={() => handleTabClick(index)}
                  theme='light'
                >
                  {tab.tabTitle}
                </TabButton>
              ))}
            </div>
          )}
        </div>
        
        {error && (
          <div style={{ textAlign: 'center', padding: '2rem 0', color: '#e74c3c' }}>
            <p className='text_body_b1'>{error}</p>
          </div>
        )}
        
        {!error && faqData.length > 0 && faqData[activeTab] && (
          <div className={styles.questions_wrapper} ref={questionsRef}>
            {faqData[activeTab].questions.map((qa: QuestionAnswer) => (
              <div className={styles.question_wrapper} key={qa.id}>
                <div className={styles.question}>
                  {renderQuestion(qa.question)}
                </div>
                <div className={styles.answer}>
                  <p className='text_body_b2'>{renderAnswer(qa.answer)}</p>
                </div>
              </div>
            ))}
          </div>
        )}
        
        <div className={styles.buttons_wrapper}>
        <div className={cn(styles.button_wrapper, styles.orange_bg)}>
          <div className={styles.button_content}>
            <h4>Остались вопросы?</h4>
          </div>
          <Button paddingType='big'
          roundType='small_rounded'
          onClick={() => window.open('https://t.me/vostok_stroy_bot', '_blank')}
          style={buttonStyle}>
            Задать вопрос в чате
          </Button>
        </div>
        <div className={cn(styles.button_wrapper, styles.blue_bg)}>
          <div className={styles.button_content}>
            <h4>Беспроцентная рассрочка</h4>
          </div>
          <Button
            paddingType='big'
            roundType='small_rounded'
            onClick={() => navigate(MORTGAGE_PAGE)}
            style={buttonStyle}
          >
            Рассчитать
          </Button>
        </div>
      </div>
      </div>

          <Footer theme={FooterType.Light} />
          <PhoneCallModal onClose={handleModal} isActive={isModalOpened} />
        </>
      )}
    </>
  );
}
